/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {Add as AddIcon} from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, {useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {AgentAbilityModal} from './AgentAbilityModal';
import {toSentenceCase} from '../../../services/utils';
import type {AgentAbility} from '../../../types/agent';
import type {AgentAbilityType} from '../hooks/useGetAgentAbilityTypes';
import {AgentActionType} from '../hooks/useGetAgentActionTypes';

const defaultAbility: AgentAbility = {
  abilityId: '',
  name: '',
  instructions: '',
  inputs: [],
  userVariables: {},
  functionType: '',
  postActions: [],
};

type Props = {
  abilities: AgentAbility[];
  setAbilities: (abilities: AgentAbility[]) => void;
  abilityTypes: AgentAbilityType[];
  actionTypes: AgentActionType[];
};

export const AgentAbilities: React.FC<Props> = ({abilities, setAbilities, abilityTypes, actionTypes}) => {
  const [abilityModalOpen, setAbilityModalOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [selectedAbilityIndex, setSelectedAbilityIndex] = useState<number | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [abilityToDelete, setAbilityToDelete] = useState<AgentAbility | null>(null);

  const handleNewAbility = () => {
    setSelectedAbilityIndex(null);
    setIsNew(true);
    setAbilityModalOpen(true);
  };

  const handleAbilityClick = (index: number) => {
    setSelectedAbilityIndex(index);
    setIsNew(false);
    setAbilityModalOpen(true);
  };

  const handleSaveAbility = (updatedAbility: AgentAbility) => {
    if (isNew) {
      setAbilities([...abilities, {...updatedAbility, abilityId: uuidv4()}]);
    } else if (selectedAbilityIndex !== null) {
      setAbilities(
        abilities.map((ability, index: number) => (index === selectedAbilityIndex ? updatedAbility : ability)),
      );
    }
    setAbilityModalOpen(false);
  };

  const handleAbilityDelete = (ability: AgentAbility, e: React.MouseEvent) => {
    e.stopPropagation();
    setAbilityToDelete(ability);
    setDeleteModalOpen(true);
  };

  const confirmAbilityDelete = () => {
    if (abilityToDelete) {
      setAbilities(abilities.filter((ability) => ability.abilityId !== abilityToDelete.abilityId));
    }
    setDeleteModalOpen(false);
    setAbilityToDelete(null);
  };

  return (
    <>
      <Stack spacing={2}>
        <Box textAlign={'right'}>
          <Button
            variant={'contained'}
            color={'primary'}
            onClick={handleNewAbility}
            startIcon={<AddIcon />}
            disabled={abilities.length >= 10}
          >
            New Ability
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Instructions</TableCell>
                <TableCell align={'right'} width={'50px'} />
              </TableRow>
            </TableHead>
            <TableBody>
              {abilities.length > 0 ? (
                abilities.map((ability, index: number) => (
                  <TableRow
                    key={ability.abilityId}
                    hover
                    onClick={() => handleAbilityClick(index)}
                    style={{cursor: 'pointer'}}
                  >
                    <TableCell width={'30%'}>{ability.name}</TableCell>
                    <TableCell width={'20%'}>{toSentenceCase(ability.functionType)}</TableCell>
                    <TableCell>{ability.instructions}</TableCell>
                    <TableCell align={'right'}>
                      <IconButton color={'error'} onClick={(e) => handleAbilityDelete(ability, e)} size={'small'}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align={'center'}>
                    <Button variant={'text'} color={'primary'} onClick={handleNewAbility} startIcon={<AddIcon />}>
                      Add an Ability
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      {abilityModalOpen && (
        <AgentAbilityModal
          open={abilityModalOpen}
          onClose={() => setAbilityModalOpen(false)}
          ability={isNew ? defaultAbility : abilities[selectedAbilityIndex!]}
          onSave={handleSaveAbility}
          abilityTypes={abilityTypes}
          actionTypes={actionTypes}
        />
      )}

      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the ability "{abilityToDelete?.name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button variant={'contained'} color={'error'} onClick={confirmAbilityDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
